import * as Notifications from 'expo-notifications';
import * as Device from 'expo-device';
import { Platform } from 'react-native';
import { Goal } from '../context/GoalsContext';

export type GoalNotificationType = 'progress' | 'deadline' | 'payment-reminder' | 'payment-due';

export interface GoalNotificationPreferences {
  goalProgress: boolean;
  deadlineNearing: boolean;
  paymentInput: boolean;
}

// Configure default notification behavior
if (Platform.OS !== 'web') {
  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldShowAlert: true,
      shouldPlaySound: true,
      shouldSetBadge: true,
    }),
  });
}

const MORNING_HOUR = 9;

function notificationId(goalId: string, type: GoalNotificationType, suffix = ''): string {
  return suffix ? `${goalId}-${type}-${suffix}` : `${goalId}-${type}`;
}

function atMorning(date: Date): Date {
  const d = new Date(date);
  d.setHours(MORNING_HOUR, 0, 0, 0);
  return d;
}

function progressPercent(goal: Goal): number {
  if (!goal.targetAmount) return 0;
  return (goal.currentAmount / goal.targetAmount) * 100;
}

export class NotificationService {
  static async registerForPushNotificationsAsync() {
    if (Platform.OS === 'web') {
      if ('Notification' in window) {
        const permission = await Notification.requestPermission();
        return permission === 'granted' ? 'web-notification-enabled' : null;
      }
      return null;
    }

    let token;

    if (Platform.OS === 'android') {
      await Notifications.setNotificationChannelAsync('default', {
        name: 'default',
        importance: Notifications.AndroidImportance.MAX,
        vibrationPattern: [0, 250, 250, 250],
        lightColor: '#FF231F7C',
      });
    }

    if (Device.isDevice) {
      const { status: existingStatus } = await Notifications.getPermissionsAsync();
      let finalStatus = existingStatus;
      if (existingStatus !== 'granted') {
        const { status } = await Notifications.requestPermissionsAsync();
        finalStatus = status;
      }
      if (finalStatus !== 'granted') {
        console.log('Failed to get push token for push notification!');
        return;
      }
      token = (await Notifications.getExpoPushTokenAsync()).data;
    } else {
      console.log('Must use physical device for Push Notifications');
    }

    return token;
  }

  /**
   * Progress milestones are event-driven (payment / amount update), not pre-scheduled on app load.
   */
  static async maybeNotifyGoalProgressMilestone(
    goal: Goal,
    previousAmount: number
  ): Promise<void> {
    const milestones = [25, 50, 75, 90];
    const prevProgress = progressPercent({ ...goal, currentAmount: previousAmount });
    const newProgress = progressPercent(goal);

    const crossed = milestones.filter(
      (milestone) => prevProgress < milestone && newProgress >= milestone
    );
    if (crossed.length === 0) return;

    const milestone = crossed[crossed.length - 1];
    const remainingAmount = goal.targetAmount - goal.currentAmount;

    if (Platform.OS === 'web') {
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(`${goal.name} Progress Update`, {
          body: `You've reached ${milestone}% of your goal! Only ${remainingAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} more to go.`,
        });
      }
      return;
    }

    await Notifications.scheduleNotificationAsync({
      identifier: notificationId(goal.id, 'progress', String(milestone)),
      content: {
        title: `${goal.name} Progress Update`,
        body: `You've reached ${milestone}% of your goal! Only ${remainingAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} more to go.`,
        data: { goalId: goal.id, type: 'progress' as GoalNotificationType },
      },
      trigger: null,
    });
  }

  /** @deprecated Use maybeNotifyGoalProgressMilestone when progress changes. */
  static async scheduleGoalProgressNotification(_goal: Goal) {
    // No-op: progress alerts fire when the user crosses a milestone, not on app startup.
  }

  static async scheduleDeadlineNotification(goal: Goal) {
    if (Platform.OS === 'web') {
      return;
    }

    const deadline = atMorning(new Date(goal.deadline));
    const now = new Date();

    if (deadline.getTime() <= now.getTime()) return;

    const reminders = [
      { days: 30, message: '1 month' },
      { days: 14, message: '2 weeks' },
      { days: 7, message: '1 week' },
      { days: 3, message: '3 days' },
      { days: 1, message: '1 day' },
    ];

    for (const reminder of reminders) {
      const triggerDate = atMorning(new Date(deadline));
      triggerDate.setDate(triggerDate.getDate() - reminder.days);

      if (triggerDate.getTime() <= now.getTime()) continue;

      await Notifications.scheduleNotificationAsync({
        identifier: notificationId(goal.id, 'deadline', String(reminder.days)),
        content: {
          title: `${goal.name} Deadline Approaching`,
          body: `Only ${reminder.message} left to reach your goal! Current progress: ${progressPercent(goal).toFixed(2)}%`,
          data: { goalId: goal.id, type: 'deadline' as GoalNotificationType },
        },
        trigger: { date: triggerDate },
      });
    }
  }

  static async schedulePaymentReminderNotification(goal: Goal) {
    if (Platform.OS === 'web') {
      return;
    }

    const now = new Date();
    const lastPayment =
      goal.payments.length > 0
        ? new Date(Math.max(...goal.payments.map((p) => new Date(p.date).getTime())))
        : null;

    const nextPaymentDate = atMorning(new Date(lastPayment || now));

    if (lastPayment) {
      nextPaymentDate.setMonth(nextPaymentDate.getMonth() + 1);
    } else {
      nextPaymentDate.setDate(nextPaymentDate.getDate() + 3);
    }

    const reminderDate = atMorning(new Date(nextPaymentDate));
    reminderDate.setDate(reminderDate.getDate() - 3);

    if (reminderDate.getTime() > now.getTime()) {
      await Notifications.scheduleNotificationAsync({
        identifier: notificationId(goal.id, 'payment-reminder'),
        content: {
          title: `Payment Due Soon for ${goal.name}`,
          body: `Your ${goal.type.toLowerCase()} payment of ${goal.monthlyContribution} is due in 3 days.`,
          data: { goalId: goal.id, type: 'payment-reminder' as GoalNotificationType },
        },
        trigger: { date: reminderDate },
      });
    }

    if (nextPaymentDate.getTime() > now.getTime()) {
      await Notifications.scheduleNotificationAsync({
        identifier: notificationId(goal.id, 'payment-due'),
        content: {
          title: `Payment Due Today for ${goal.name}`,
          body: `Don't forget to make your ${goal.type.toLowerCase()} payment of ${goal.monthlyContribution} today.`,
          data: { goalId: goal.id, type: 'payment-due' as GoalNotificationType },
        },
        trigger: { date: nextPaymentDate },
      });
    }
  }

  static async syncGoalNotifications(
    goals: Goal[],
    preferences: GoalNotificationPreferences
  ): Promise<void> {
    if (Platform.OS === 'web') {
      return;
    }

    const anyEnabled =
      preferences.goalProgress ||
      preferences.deadlineNearing ||
      preferences.paymentInput;

    if (!anyEnabled) {
      await this.cancelAllNotifications();
      return;
    }

    for (const goal of goals) {
      await this.cancelGoalNotifications(goal.id);

      if (preferences.deadlineNearing) {
        await this.scheduleDeadlineNotification(goal);
      }

      if (preferences.paymentInput) {
        await this.schedulePaymentReminderNotification(goal);
      }
    }
  }

  static async cancelAllNotifications() {
    if (Platform.OS === 'web') {
      return;
    }
    await Notifications.cancelAllScheduledNotificationsAsync();
  }

  static async cancelGoalNotifications(goalId: string) {
    if (Platform.OS === 'web') {
      return;
    }
    const scheduledNotifications = await Notifications.getAllScheduledNotificationsAsync();
    for (const notification of scheduledNotifications) {
      const data = notification.content.data;
      if (data?.goalId === goalId || notification.identifier.startsWith(`${goalId}-`)) {
        await Notifications.cancelScheduledNotificationAsync(notification.identifier);
      }
    }
  }
}

export function buildNotificationSyncKey(
  preferences: GoalNotificationPreferences,
  goals: Goal[]
): string {
  return JSON.stringify({
    preferences,
    goals: goals.map((g) => ({
      id: g.id,
      deadline: g.deadline,
      monthlyContribution: g.monthlyContribution,
      paymentCount: g.payments.length,
      lastPayment: g.payments.length
        ? Math.max(...g.payments.map((p) => new Date(p.date).getTime()))
        : 0,
    })),
  });
}
